// Global events manager

import { ColorThemeName } from "./app-preferences";
import type { ActionModalOptions } from "@/types/action-modal";

type CallbackFunctionVariadic = (...args: any[]) => void;

/**
 * Mapping of App events
 */
export type AppEventsMap = {
    /**
     * Event sent when AuthController changes its status
     */
    "auth-status-changed": () => void;

    /**
     * Event sent when AuthController changes its loading status
     * @param loading True if the auth status is being loaded
     */
    "auth-status-loading": (loading: boolean) => void;

    /**
     * Event sent when an Unauthorized (401) status code is received
     * This may indicate the session has expired
     */
    unauthorized: () => void;

    /**
     * Event sent when a locale is selected
     * @param locale The locale identifier
     */
    "set-locale": (locale: string) => void;

    /**
     * Event sent when a locale is loaded
     * @param locale The locale identifier
     */
    "loaded-locale": (locale: string) => void;

    /**
     * Event send when the router loading status changes
     * @param loading True if loading
     */
    "router-load-state-change": (loading: boolean) => void;

    /**
     * Event sent when the theme changes
     * @param theme The theme name
     */
    "theme-changed": (theme: ColorThemeName) => void;

    /**
     * Event sent to show a snackbar
     * @param message The message
     * @param position The position
     * @param error If the message is an error
     */
    snack: (message: string, position?: "left" | "center" | "right", error?: boolean, warning?: boolean) => void;

    /**
     * Event sent to show a message modal
     * @param title The title
     * @param message The message
     */
    "msg-modal": (title: string, message: string) => void;

    /**
     * Event sent to ask for user confirmation
     * @param options The options
     */
    "ask-confirmation": (options: {
        title: string;
        message: string;
        danger?: boolean;
        askWalletPassword?: boolean;
        callback: (pw?: string) => void;
    }) => void;

    /**
     * Event sent to open the ActionModal
     * @param options The options
     */
    "action-modal-open": (options: ActionModalOptions) => void;

    /**
     * Show profile modal
     * @param uid User ID
     */
    "show-profile": (uid: string) => void;

    /**
     * Show participant modal
     * @param id Participant ID
     */
    "show-participant": (id: string) => void;

    /**
     * Notifications count checked
     * @param count Count unread notifications
     */
    "notification-count-update": (count: number) => void;

    /**
     * The source types have been loaded
     */
    "source-types-loaded": () => void;

    /**
     * The sink types have been loaded
     */
    "sink-types-loaded": () => void;

    /**
     * The data space configuration check was updated
     */
    "ds-config-check-updated": () => void;

    /**
     * The data space configuration was updated
     */
    "ds-config-updated": () => void;

    /**
     * Request to open the data space configuration modal
     */
    "ds-config-modal-open": () => void;

    /**
     * Show dynamic modal with custom component
     * @param options Modal options including title, component and props
     */
    "show-dynamic-modal": (options: { title: string; component: any; props?: any }) => void;

    /**
     * Event sent when a data source is deleted
     * @param id The data source ID
     */
    "data-source-deleted": (id: string) => void;

    /**
     * Event sent when a data sink is deleted
     * @param id The data sink ID
     */
    "data-sink-deleted": (id: string) => void;

    /**
     * Event sent when a catalog is deleted
     * @param id The catalog ID
     */
    "catalog-deleted": (id: string) => void;

    /**
     * Event sent when a pipeline is deleted
     * @param id The pipeline ID
     */
    "pipeline-deleted": (id: string) => void;

    /**
     * Event sent when a pipeline is renamed
     * @param id The pipeline ID
     */
    "pipeline-renamed": (id: string) => void;

    /**
     * Event sent when a data app is deleted
     * @param id The data app ID
     */
    "data-app-deleted": (id: string) => void;

    /**
     * Event sent when a wallet is deleted
     * @param id The wallet ID
     */
    "wallet-deleted": (id: string) => void;

    /**
     * Event sent when a DID public key requires reloading its details
     */
    "did-public-key-details-reloaded": () => void;

    /**
     * Event sent when a DID public key  requires reloading its networks and wallets
     */
    "did-public-key-network-wallets-reloaded": () => void;

    /**
     * Event sent when a DID public key requires reloading its networks
     */
    "did-public-key-network-reloaded": () => void;

    /**
     * Event sent when a DID public key requires reloading its wallets
     */
    "did-public-key-wallets-reloaded": () => void;

    /**
     * Event sent when the status of a negotiation change and the page must be reload
     */
    "reload-negotiation-page": () => void;

    "reload-transfer-page": () => void;

    /**
     * Opens the LLM chat assistant
     */
    "llm-chat-assistant-open": () => void;

    /**
     * Event sent when the context for the LLM changes
     */
    "llm-context-changed": () => void;

    /**
     * Event sent when a data source is created (e.g. via import)
     * @param id The data source ID
     */
    "data-source-created": (id: string) => void;

    /**
     * Event sent when a data sink is created (e.g. via import)
     * @param id The data sink ID
     */
    "data-sink-created": (id: string) => void;
};

/**
 * Global events manager
 */
export class AppEvents {
    public static events: { [key: string]: CallbackFunctionVariadic[] } = {};

    /**
     * Listens for global event
     * @param eventName The event name
     * @param handler The event handler
     */
    public static AddEventListener<K extends keyof AppEventsMap>(eventName: K, handler: AppEventsMap[K]) {
        if (!AppEvents.events[eventName]) {
            AppEvents.events[eventName] = [];
        }
        AppEvents.events[eventName].push(handler);
    }

    /**
     * Emits global event
     * @param eventName The event name
     * @param args The event arguments
     */
    public static Emit<K extends keyof AppEventsMap>(eventName: K, ...args: Parameters<AppEventsMap[K]>) {
        if (AppEvents.events[eventName]) {
            for (const handler of AppEvents.events[eventName]) {
                try {
                    (handler as (...a: any[]) => void)(...args);
                } catch (ex) {
                    console.error(ex);
                }
            }
        }
    }

    /**
     * Removes event listener
     * @param eventName The event name
     * @param handler The event handler
     */
    public static RemoveEventListener<K extends keyof AppEventsMap>(eventName: K, handler: AppEventsMap[K]) {
        if (!AppEvents.events[eventName]) {
            return;
        }
        const i = AppEvents.events[eventName].indexOf(handler);
        if (i >= 0) {
            AppEvents.events[eventName].splice(i, 1);
            if (AppEvents.events[eventName].length === 0) {
                delete AppEvents.events[eventName];
            }
        }
    }
}
